import taapi from './taapi'
import { addPendingTask, dispatchCompletedTask, fetchTasks, kickTaskstream, patchQuestionProcessing, setQuestionProcessing } from './TaskManagerService'
import type { Project, Question, BatchProcessArgs } from '../types'
import { BatchType } from '../types/BatchProcessArgs'
import { UpdateQuestionFn } from '../common/common'
import { QuestionProcessingState } from '../types/QuestionProcessingState'
import { addToast } from './Toasts'

// ─── Centralised per-question actions ────────────────────────────────────────
// Both BidQuestion's main toolbar and the BidQuestionsLeftPanel row icons go
// through this module so the API calls + result-merging logic live in one
// place. Processing-state flips go through the TaskManagerService registry,
// keyed by questionsha - no per-call patchProcessing callback gets threaded
// down any more.

export interface ActionResult {
  success: boolean
  msg?: string
}


// ─── Batch action executor ───────────────────────────────────────────────────
// Single entry point for any combination of sources / structure /
// answer / trace / score. Mirrors the C# BatchProcessParameters where
// `batchtypestring = "workflow"` drives the full pipeline and the gen*
// flags pick which substeps actually run. Shared by
// BidBatchProcessModal's Generate / Trace / Score / Workflow modes and
// any per-row trigger in the question list.

// Per-step overrides for executeGenerateBatch. All optional - defaults
// mirror the original "generate everything" behaviour (sources +
// structure + answer, no trace, no scoring). Set individual flags to
// flip substeps on / off.
export interface GenerateBatchOptions {
  pickselected?: string   // 'auto' | 'all' | 'none' | 'project'
  n?: number
  pickauto?: boolean
  gensources?: boolean
  genstructure?: boolean
  genanswer?: boolean
  gentrace?: boolean
  genscore?: boolean
  genwintheme?: boolean
  genimprovements?: boolean
}

export async function executeGenerateBatch(
  questions: Question[],
  project: Project,
  styleName: string | undefined,
  updateQuestion: UpdateQuestionFn,
  opts: GenerateBatchOptions = {},
): Promise<{ success: boolean; ok: number }> {
  const gensources = opts.gensources ?? true
  const genstructure = opts.genstructure ?? true
  const genanswer = opts.genanswer ?? true
  const gentrace = opts.gentrace ?? false
  const genscore = opts.genscore ?? false

  const answerActive = gensources || genstructure || genanswer

  // Per-substep dispatch / completion patches. Each gen flag flips its
  // own queued/running pair so the matching button / row spinner lights
  // up immediately and clears once the task lands in tkinit.
  const dispatchPatch: Partial<QuestionProcessingState> = {
    updatetime: Date.now(),
    log: [{ ts: Date.now(), step: 'started', text: 'Queued' }],
  }
  const onCompletePatch: Partial<QuestionProcessingState> = {
    log: [{ ts: Date.now(), step: 'end', text: 'Done' }],
  }
  if (answerActive) {
    dispatchPatch.queuedAnswer = true
    dispatchPatch.runningAnswer = false
    dispatchPatch.sourcedocs = gensources
    dispatchPatch.structure = genstructure
    dispatchPatch.answer = genanswer
    onCompletePatch.queuedAnswer = false
    onCompletePatch.runningAnswer = false
  }
  if (gentrace) {
    dispatchPatch.queuedTrace = true
    dispatchPatch.runningTrace = false
    onCompletePatch.queuedTrace = false
    onCompletePatch.runningTrace = false
  }
  if (genscore) {
    dispatchPatch.queuedScoring = true
    dispatchPatch.runningScoring = false
    onCompletePatch.queuedScoring = false
    onCompletePatch.runningScoring = false
  }

  const args: Partial<BatchProcessArgs> = {
    projectsha: project.projectsha,
    questions,
    batchtypestring: BatchType.workflow,
    pickselected: opts.pickselected ?? 'auto',
    n: opts.n ?? 3,
    stylename: styleName ?? 'Default',
    gensources,
    genstructure,
    genanswer,
    gentrace,
    genscore,
    pickauto: opts.pickauto ?? true,
    projectbideval: (project as any).projectbideval,
  }

  return runBatch({
    args,
    updateQuestion,
    dispatchPatch,
    onCompletePatch,
    startedLabel: 'Workflow started',
    startedLabelMulti: (n: number) => `Workflow started on ${n} questions`,
    failureLabel: 'Failed to start workflow',
  })
}

// Shared pmbatchprocess driver. Kicks the SSE consumer so the loop is
// alive before the backend starts emitting events, posts the supplied
// BatchProcessArgs payload, applies the per-question dispatch patch to
// processingState, registers each tasksha with the pending-task poller
// (with an optional onComplete cleanup), and forces an immediate tkinit
// refresh so completion lands in the next render frame.
async function runBatch(opts: {
  args: Partial<BatchProcessArgs>
  updateQuestion: UpdateQuestionFn
  dispatchPatch: Partial<QuestionProcessingState>
  onCompletePatch: Partial<QuestionProcessingState>
  startedLabel: string
  startedLabelMulti: (n: number) => string
  failureLabel: string
}): Promise<{ success: boolean; ok: number }> {
  const { args, updateQuestion } = opts
  const questions = args.questions ?? []
  if (questions.length === 0) {
    addToast('error', 'No questions selected')
    return { success: false, ok: 0 }
  }
  if (!args.projectsha) {
    addToast('error', 'No project')
    return { success: false, ok: 0 }
  }

  kickTaskstream()

  const result = await taapi.post('/api2/pmbatchprocess', args)
  if (!result?.success) {
    addToast('error', result?.msg ?? opts.failureLabel)
    return { success: false, ok: 0 }
  }
  const taskshalist: string[] = result?.taskshalist ?? []

  questions.forEach((q, i) => {
    patchQuestionProcessing(q.questionsha, opts.dispatchPatch)
    const sha = taskshalist[i]
    if (sha) {
      addPendingTask(sha, addToast, updateQuestion, () => {
      })
    }
  })
  fetchTasks(taskshalist, addToast, updateQuestion)

  const single = questions.length === 1
  addToast('success', single ? opts.startedLabel : opts.startedLabelMulti(questions.length))
  return { success: true, ok: questions.length }
}

// ── Trace ──────────────────────────────────────────────────────────────────
// POSTs /api2/pmforegroundprocess with batchtypestring 'tracing'.
// Flips `runningTrace` for the spinner; runForeground does the actual
// post + dispatchCompletedTask.
export async function runTrace(
  question: Question,
  project: Project,
  updateQuestion: UpdateQuestionFn,
): Promise<ActionResult> {
  console.log('runTrace');
  return runForeground(question, project, BatchType.tracing, 'runningTrace', updateQuestion)
}

// ── Score ──────────────────────────────────────────────────────────────────
// Same shape as runTrace but with batchtypestring 'scoring' and the
// `runningScoring` spinner flag.
export async function runScore(
  question: Question,
  project: Project,
  updateQuestion: UpdateQuestionFn,
): Promise<ActionResult> {
  console.log('runScore');
  return runForeground(question, project, BatchType.scoring, 'runningScoring', updateQuestion)
}

// ── Suggest Improvements ───────────────────────────────────────────────────
// Same foreground driver as scoring / tracing — flips the
// `genimprovements` flag on the pmforegroundprocess args so the server
// runs the improvement-suggestion pass against the question's
// existing bideval. Result lands on the question as an updated
// bideval (improvementcategories baked into each group) via
// dispatchCompletedTask's 'improvements' handler.
export async function runImprove(
  question: Question,
  project: Project,
  updateQuestion: UpdateQuestionFn,
): Promise<ActionResult> {
  console.log('runImprove');
  return runForeground(question, project, BatchType.improvements, 'runningImprovements', updateQuestion)
}

// Shared single-question foreground driver. POSTs /api2/pmforegroundprocess
// (the synchronous variant of pmbatchprocess) for one question, hands the
// returned task straight to dispatchCompletedTask, and flips the named
// running flag on entry / off in the finally so the spinner clears the
// moment the call resolves. No tkinit roundtrip - foreground responses
// carry the completed task directly.
async function runForeground(
  question: Question,
  project: Project,
  batchtypestring: BatchType,
  runningField: keyof QuestionProcessingState,
  updateQuestion: UpdateQuestionFn,
): Promise<ActionResult> {
  if (!project.projectsha) {
    return { success: false, msg: 'No project' }
  }

  setQuestionProcessing(question.questionsha, runningField, true)
  try {

    const args: Partial<BatchProcessArgs> = {
      projectsha: project.projectsha,
      questions: [question],
      batchtypestring: BatchType.workflow,
      gentrace: batchtypestring === BatchType.tracing,
      genscore: batchtypestring === BatchType.scoring,
      genwintheme: batchtypestring === BatchType.wintheme,
      genimprovements: batchtypestring === BatchType.improvements,
      projectbideval: (project as any).projectbideval,
    }
    const result = await taapi.post('/api2/pmforegroundprocess', args)
    if (!result?.success) return { success: false, msg: result?.msg }
    dispatchCompletedTask(result, addToast, updateQuestion)
    return { success: true }
  } catch (e) {
    return { success: false, msg: String(e) }
  } finally {
    setQuestionProcessing(question.questionsha, runningField, false)
  }
}
